/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SortIcon from '@mui/icons-material/Sort';
import { EmptyState } from '../../../components/v2';
import { chipColor, getRegra, sortRanking } from './constants';
import ResumoHoras from './ResumoHoras';

const SORT_OPTIONS = [
  { mode: 'NOME_ASC', title: 'Nome A–Z', field: 'nome', direction: 'asc' },
  { mode: 'NOME_DESC', title: 'Nome Z–A', field: 'nome', direction: 'desc' },
  { mode: 'DIAS_DESC', title: 'Dias c/ violação ↓', field: 'diasViolacao', direction: 'desc' },
  { mode: 'DIAS_ASC', title: 'Dias c/ violação ↑', field: 'diasViolacao', direction: 'asc' },
  { mode: 'REGRAS_DESC', title: 'Regras ↓', field: 'regras', direction: 'desc' },
  { mode: 'REGRAS_ASC', title: 'Regras ↑', field: 'regras', direction: 'asc' },
];

const RankingTable = ({
  rows,
  loading,
  onOpenCalendario,
  onOpenFolha,
  onOpenDias,
  onOpenConformidade,
  canOpenConformidade,
}) => {
  const [sortMode, setSortMode] = useState('NOME_ASC');
  const [anchorEl, setAnchorEl] = useState(null);
  const sort = SORT_OPTIONS.find((option) => option.mode === sortMode) || SORT_OPTIONS[0];
  const sorted = useMemo(
    () => sortRanking(rows, sort.field, sort.direction),
    [rows, sort.field, sort.direction],
  );

  if (!loading && rows.length === 0) {
    return (
      <EmptyState
        title="Nenhum inspetor neste recorte"
        message="Ninguém com a regra selecionada no período. Limpe o filtro do tile ou alargue as datas."
      />
    );
  }

  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{ px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}
      >
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<SortIcon />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          Ordenar
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem
              key={option.mode}
              selected={sortMode === option.mode}
              onClick={() => {
                setSortMode(option.mode);
                setAnchorEl(null);
              }}
            >
              {option.title}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Inspetor</TableCell>
          <TableCell>Regime</TableCell>
          <TableCell align="right">Dias c/ violação</TableCell>
          <TableCell align="right">H. extra fora do teto</TableCell>
          <TableCell>Regras</TableCell>
          <TableCell align="right">Ações</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((row) => (
          <TableRow key={row.id} hover>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.nome}</Typography>
              <ResumoHoras row={row} compact />
            </TableCell>
            <TableCell>{row.regime}</TableCell>
            <TableCell align="right">
              {row.diasViolacao > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenDias(row);
                  }}
                  sx={{ minWidth: 0, fontWeight: 700 }}
                >
                  {row.diasViolacao}
                </Button>
              ) : row.diasViolacao}
            </TableCell>
            <TableCell align="right">{row.horaExtraForaTeto}</TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {row.regras.map((regraId) => {
                  const regra = getRegra(regraId);
                  if (!regra) return null;
                  return (
                    <Chip
                      key={regraId}
                      size="small"
                      label={`${regra.short} · ${regra.label}`}
                      color={chipColor(regraId)}
                      variant={regraId === 'SEM_JORNADA' ? 'outlined' : 'filled'}
                    />
                  );
                })}
              </Stack>
            </TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={0.5} justifyContent="flex-end" useFlexGap sx={{ flexWrap: 'wrap' }}>
                {canOpenConformidade && onOpenConformidade && (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<FactCheckOutlinedIcon />}
                    onClick={() => onOpenConformidade(row)}
                  >
                    Conformidade
                  </Button>
                )}
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<CalendarMonthOutlinedIcon />}
                  onClick={() => onOpenCalendario(row)}
                >
                  Calendário
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<OpenInNewOutlinedIcon />}
                  onClick={() => onOpenFolha(row)}
                >
                  Folha de ponto
                </Button>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      </Table>
    </TableContainer>
  );
};

RankingTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onOpenCalendario: PropTypes.func.isRequired,
  onOpenFolha: PropTypes.func.isRequired,
  onOpenDias: PropTypes.func.isRequired,
  onOpenConformidade: PropTypes.func,
  canOpenConformidade: PropTypes.bool,
};

export default RankingTable;
